import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import i18n from "@/i18n";
import api, { formatApiError, tokenStore } from "@/lib/api";
import { setBootstrap, clearBootstrap } from "@/lib/bootstrap";

const AuthCtx = createContext(null);

// Apply the language stored on the user (set from their country at registration).
// On `refreshMe` and `login` we honour the localStorage choice (the user may have
// manually picked a language in this browser). On `register` we ALWAYS force the
// country-derived language so newly-onboarded users see the dashboard in their
// own language regardless of the browser locale.
function applyUserLanguage(userObj, { force = false } = {}) {
  if (!userObj || !userObj.language) return;
  try {
    const stored = localStorage.getItem("op_lang");
    if (force || !stored) {
      if (userObj.language !== i18n.language) {
        i18n.changeLanguage(userObj.language);
      }
      if (force) {
        try { localStorage.setItem("op_lang", userObj.language); } catch {}
      }
    }
  } catch { /* localStorage unavailable */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking | object | false
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      // Single bootstrap call returns user + accounts + public settings.
      const { data } = await api.get("/auth/bootstrap");
      setBootstrap({ accounts: data.accounts, settings: data.settings });
      setUser(data.user);
      applyUserLanguage(data.user);
    } catch {
      setUser(false);
      tokenStore.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Global session-expired event from axios interceptor
  useEffect(() => {
    const handler = () => { setUser(false); tokenStore.clear(); };
    window.addEventListener("op:session-expired", handler);
    return () => window.removeEventListener("op:session-expired", handler);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) tokenStore.set(data.access_token);
      setBootstrap({ accounts: data.accounts, settings: data.settings });
      setUser(data.user);
      applyUserLanguage(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) tokenStore.set(data.access_token);
      setUser(data.user);
      applyUserLanguage(data.user, { force: true });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    clearBootstrap();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshMe, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
